/**
 * Zustand store for Metadata V1 Editor
 * Manages multi-file editing with tabs, dirty tracking, and preview mode
 *
 * ORGANIZATION SCOPING
 * --------------------
 * Editor tabs hold one organization's metadata: its file ids, its source, and
 * the user's unsaved edits to it. Showing any of that under another
 * organization is the most alarming form of this leak, because the content is
 * real and the org it is labelled with is wrong.
 *
 * Tabs are partitioned by organization ({@link MetadataEditorState.workspaceByOrg}).
 * The store's top-level fields are the ACTIVE organization's workspace; on a
 * switch the current workspace is stashed under the org it belongs to and the
 * incoming org's workspace is loaded in its place.
 *
 * Stashing rather than clearing is deliberate. A dirty tab is unsaved user work;
 * discarding it on an org switch would be silent data loss, and prompting on
 * every switch would make switching hostile. Keeping it parked under its own org
 * means the work is exactly where the user left it when they come back, and is
 * unreachable — not merely hidden — from anywhere else.
 *
 * Tab-addressed updates (`syncTabContentFromServer`, `setTabError`, …) are safe
 * against late responses by construction: tab ids are minted per open, so a
 * response for an Org A tab finds no such tab in Org B's workspace and no-ops.
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
// Subpath, not the barrel: the barrel pulls in Monaco and registers the Apex
// language as an import side effect, which a store must not drag along.
import { applyTabOrder } from '@ncoder/code-editor/tab-order';
import {
  dropOrgSlice,
  readOrgSlice,
  registerOrgScopedState,
  writeOrgSlice,
  type OrgId,
} from '@/lib/org-scope';
import type { FileNode } from '@/components/molecules/metadata_v1';
import type { MetadataBranch } from '@/services/metadata_v1/types';

export interface EditorTab {
  id: string;
  file: FileNode;
  content: string;           // Current content (may have unsaved changes)
  originalContent: string;   // Content when opened (for dirty comparison)
  isDirty: boolean;
  isPinned: boolean;
  isPreview: boolean;        // Preview tabs are italicized and get replaced
  isLoading: boolean;
  isSaving: boolean;         // True when save is in progress
  isEdited: boolean;         // From API - indicates file is ready to deploy
  error: Error | null;
  // Branch state
  activeBranch: MetadataBranch | null;  // null = no branch, editing main
  viewingMain: boolean;                  // true = showing main content read-only
  mainContent: string;                   // cached main content for View Main toggle
  // GitHub state
  viewingGithub: boolean;               // true = showing GitHub content read-only
  githubContent: string | null;          // fetched GitHub file content
  // Bundle navigation
  bundleSiblings?: FileNode[];           // Other files in the same LWC/Aura bundle
}

interface PendingClose {
  tabId: string;
  resolve: (shouldClose: boolean) => void;
}

/**
 * Everything in the editor that belongs to one organization.
 *
 * The store spreads the active organization's workspace across its own top-level
 * fields so components read `state.tabs`, not `state.workspace.tabs`; this type
 * is what gets parked in {@link MetadataEditorState.workspaceByOrg} on a switch.
 */
export interface EditorWorkspace {
  tabs: EditorTab[];
  activeTabId: string | null;
  pendingClose: PendingClose | null;  // For close confirmation dialog

  // Filter state - which metadata types to show
  // null = show all (default), Set = show only those types (empty Set = hide all)
  visibleMetadataTypes: Set<string> | null;
  isFilterModalOpen: boolean;
}

function emptyWorkspace(): EditorWorkspace {
  return {
    tabs: [],
    activeTabId: null,
    pendingClose: null,
    visibleMetadataTypes: null,  // null = show all (default)
    isFilterModalOpen: false,
  };
}

interface MetadataEditorState extends EditorWorkspace {
  /**
   * Workspaces for organizations other than the active one, parked until the
   * user returns. The active organization's workspace lives in the fields above,
   * not here, so there is exactly one copy of it at any moment.
   */
  workspaceByOrg: Map<string, EditorWorkspace>;

  // Actions
  openFile: (file: FileNode, content: string, isPreview?: boolean, isEdited?: boolean) => void;
  closeTab: (tabId: string) => void;
  closeTabForce: (tabId: string) => void;  // Close without confirmation
  closeOtherTabs: (tabId: string) => void;
  closeAllTabs: () => void;
  closeSavedTabs: () => void;             // Close all non-dirty tabs
  setActiveTab: (tabId: string) => void;
  /**
   * Apply a new tab order (drag-to-reorder, Alt+Arrow), as a complete list of
   * tab ids. Order is application state, so the strip proposes and this decides;
   * unknown ids are ignored and unmentioned tabs keep their relative position.
   */
  reorderTabs: (orderedTabIds: string[]) => void;
  updateContent: (tabId: string, content: string) => void;
  markSaved: (tabId: string, newContent?: string, isEdited?: boolean) => void;
  setTabSaving: (tabId: string, isSaving: boolean) => void;
  setTabEdited: (tabId: string, isEdited: boolean) => void;
  pinTab: (tabId: string) => void;
  unpinTab: (tabId: string) => void;
  promotePreviewTab: (tabId: string) => void;  // Convert preview to permanent
  setTabLoading: (tabId: string, isLoading: boolean) => void;
  setTabError: (tabId: string, error: Error | null) => void;
  
  // For close confirmation dialog
  confirmClose: (shouldClose: boolean) => void;
  getPendingCloseTab: () => EditorTab | null;
  
  // Filter actions
  setFilterModalOpen: (isOpen: boolean) => void;
  toggleMetadataTypeFilter: (metadataType: string, allTypes?: string[]) => void;
  setVisibleMetadataTypes: (types: Set<string> | null) => void;
  clearFilters: () => void;
  selectAllFilters: (allTypes: string[]) => void;
  isMetadataTypeVisible: (metadataType: string) => boolean;
  
  // Selectors
  getActiveTab: () => EditorTab | null;
  getTabByFileId: (fileId: string) => EditorTab | null;
  hasDirtyTabs: () => boolean;
  getEditedTabs: () => EditorTab[];  // Get all tabs with isEdited = true (ready to deploy)
  
  // Branch actions
  setBranchForTab: (tabId: string, branch: MetadataBranch | null) => void;
  setViewingMain: (tabId: string, viewingMain: boolean) => void;
  setMainContentForTab: (tabId: string, mainContent: string) => void;
  // GitHub actions
  setViewingGithub: (tabId: string, viewingGithub: boolean) => void;
  setGithubContentForTab: (tabId: string, githubContent: string | null) => void;

  // Bundle navigation
  setBundleSiblingsForTab: (tabId: string, siblings: FileNode[]) => void;

  // Sync server content to tab (used when React Query refetches)
  // Only updates if tab is not dirty (no unsaved changes)
  syncTabContentFromServer: (fileId: string, serverContent: string, isEdited?: boolean) => boolean;
  
  /**
   * Discard the ACTIVE organization's workspace. Other organizations' parked
   * workspaces are untouched — this is "close everything", not "sign out".
   */
  reset: () => void;
}

export const useMetadataEditorStore = create<MetadataEditorState>()(
  devtools(
    (set, get) => ({
      ...emptyWorkspace(),
      workspaceByOrg: new Map(),

      openFile: (file, content, isPreview = true, isEdited = false) => {
        const state = get();
        
        // Check if file is already open
        const existingTab = state.tabs.find(t => t.file.id === file.id);
        if (existingTab) {
          set({ activeTabId: existingTab.id });
          return;
        }
        
        // If preview mode and there's an existing preview tab, replace it
        const existingPreviewTab = state.tabs.find(t => t.isPreview && !t.isDirty);
        
        const newTab: EditorTab = {
          id: crypto.randomUUID(),
          file,
          content,
          originalContent: content,
          isDirty: false,
          isPinned: false,
          isPreview,
          isLoading: false,
          isSaving: false,
          isEdited,
          error: null,
          activeBranch: null,
          viewingMain: false,
          mainContent: content,
          viewingGithub: false,
          githubContent: null,
        };
        
        if (isPreview && existingPreviewTab) {
          // Replace the existing preview tab
          set({
            tabs: state.tabs.map(t => 
              t.id === existingPreviewTab.id ? newTab : t
            ),
            activeTabId: newTab.id,
          });
        } else {
          // Add new tab
          set({
            tabs: [...state.tabs, newTab],
            activeTabId: newTab.id,
          });
        }
      },

      closeTab: (tabId) => {
        const state = get();
        const tab = state.tabs.find(t => t.id === tabId);
        
        if (!tab) return;
        
        // If dirty (and not in a read-only view), show confirmation dialog
        if (tab.isDirty && !tab.viewingMain && !(tab.viewingGithub && !!tab.activeBranch)) {
          set({
            pendingClose: {
              tabId,
              resolve: (shouldClose) => {
                if (shouldClose) {
                  get().closeTabForce(tabId);
                }
                set({ pendingClose: null });
              },
            },
          });
          return;
        }
        
        // Not dirty, close immediately
        get().closeTabForce(tabId);
      },

      closeTabForce: (tabId) => {
        const state = get();
        const tabIndex = state.tabs.findIndex(t => t.id === tabId);
        
        if (tabIndex === -1) return;
        
        const remaining = state.tabs.filter(t => t.id !== tabId);
        
        // Determine next active tab
        let nextActiveId = state.activeTabId;
        if (state.activeTabId === tabId) {
          if (remaining.length > 0) {
            // Prefer tab to the right, then left
            const nextIndex = Math.min(tabIndex, remaining.length - 1);
            nextActiveId = remaining[nextIndex].id;
          } else {
            nextActiveId = null;
          }
        }
        
        set({
          tabs: remaining,
          activeTabId: nextActiveId,
        });
      },

      closeOtherTabs: (tabId) => {
        const state = get();
        const dirtyOthers = state.tabs.filter(t => t.id !== tabId && t.isDirty);
        
        if (dirtyOthers.length > 0) {
          // For now, just keep dirty tabs and close others
          set({
            tabs: state.tabs.filter(t => t.id === tabId || t.isDirty),
            activeTabId: tabId,
          });
        } else {
          set({
            tabs: state.tabs.filter(t => t.id === tabId),
            activeTabId: tabId,
          });
        }
      },

      closeAllTabs: () => {
        const state = get();
        const dirtyTabs = state.tabs.filter(t => t.isDirty);
        
        if (dirtyTabs.length > 0) {
          // Keep only dirty tabs
          set({ tabs: dirtyTabs, activeTabId: dirtyTabs[0]?.id || null });
        } else {
          set({ tabs: [], activeTabId: null });
        }
      },

      closeSavedTabs: () => {
        const state = get();
        const dirtyTabs = state.tabs.filter(t => t.isDirty);
        const wasActiveTabDirty = state.tabs.find(t => t.id === state.activeTabId)?.isDirty;
        
        set({
          tabs: dirtyTabs,
          activeTabId: wasActiveTabDirty ? state.activeTabId : (dirtyTabs[0]?.id || null),
        });
      },

      setActiveTab: (tabId) => {
        const state = get();
        const tab = state.tabs.find(t => t.id === tabId);
        if (tab) {
          set({ activeTabId: tabId });
        }
      },

      reorderTabs: (orderedTabIds) => {
        const state = get();
        const reordered = applyTabOrder(state.tabs, orderedTabIds, (t) => t.id);
        // Reordering moves seats, never occupants: the active tab keeps its
        // identity, and `applyTabOrder` guarantees the same set of tabs comes
        // back — a stale ordering can misplace a tab but cannot drop one.
        set({ tabs: reordered });
      },

      updateContent: (tabId, content) => {
        set((state) => ({
          tabs: state.tabs.map(t => {
            if (t.id !== tabId) return t;
            
            const isDirty = content !== t.originalContent;
            return {
              ...t,
              content,
              isDirty,
              // If user edits, promote from preview
              isPreview: isDirty ? false : t.isPreview,
            };
          }),
        }));
      },

      markSaved: (tabId, newContent, isEdited) => {
        set((state) => ({
          tabs: state.tabs.map(t => {
            if (t.id !== tabId) return t;
            const content = newContent ?? t.content;
            return {
              ...t,
              content,
              originalContent: content,
              isDirty: false,
              isSaving: false,
              isEdited: isEdited ?? t.isEdited,
            };
          }),
        }));
      },

      setTabSaving: (tabId, isSaving) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, isSaving } : t
          ),
        }));
      },

      setTabEdited: (tabId, isEdited) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, isEdited } : t
          ),
        }));
      },

      pinTab: (tabId) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, isPinned: true, isPreview: false } : t
          ),
        }));
      },

      unpinTab: (tabId) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, isPinned: false } : t
          ),
        }));
      },

      promotePreviewTab: (tabId) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, isPreview: false } : t
          ),
        }));
      },

      setTabLoading: (tabId, isLoading) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, isLoading } : t
          ),
        }));
      },

      setTabError: (tabId, error) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, error, isLoading: false } : t
          ),
        }));
      },

      confirmClose: (shouldClose) => {
        const state = get();
        if (state.pendingClose) {
          state.pendingClose.resolve(shouldClose);
        }
      },

      getPendingCloseTab: () => {
        const state = get();
        if (!state.pendingClose) return null;
        return state.tabs.find(t => t.id === state.pendingClose!.tabId) || null;
      },

      getActiveTab: () => {
        const state = get();
        return state.tabs.find(t => t.id === state.activeTabId) || null;
      },

      getTabByFileId: (fileId) => {
        const state = get();
        return state.tabs.find(t => t.file.id === fileId) || null;
      },

      hasDirtyTabs: () => {
        return get().tabs.some(t => t.isDirty);
      },

      // Filter actions
      setFilterModalOpen: (isOpen) => {
        set({ isFilterModalOpen: isOpen });
      },

      toggleMetadataTypeFilter: (metadataType, allTypes?: string[]) => {
        const state = get();
        // If null (show all), we need all types to toggle one off
        const currentVisible = state.visibleMetadataTypes === null && allTypes
          ? new Set(allTypes)
          : new Set(state.visibleMetadataTypes || []);
        
        if (currentVisible.has(metadataType)) {
          currentVisible.delete(metadataType);
        } else {
          currentVisible.add(metadataType);
        }
        set({ visibleMetadataTypes: currentVisible });
      },

      setVisibleMetadataTypes: (types) => {
        set({ visibleMetadataTypes: types });
      },

      clearFilters: () => {
        set({ visibleMetadataTypes: new Set<string>() });
      },

      selectAllFilters: (allTypes) => {
        set({ visibleMetadataTypes: new Set(allTypes) });
      },

      isMetadataTypeVisible: (metadataType) => {
        const state = get();
        // null = show all, Set = show only those types
        if (state.visibleMetadataTypes === null) return true;
        return state.visibleMetadataTypes.has(metadataType);
      },

      getEditedTabs: () => {
        return get().tabs.filter(t => t.isEdited);
      },

      setBranchForTab: (tabId, branch) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, activeBranch: branch, viewingMain: false, viewingGithub: false } : t
          ),
        }));
      },

      setViewingMain: (tabId, viewingMain) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, viewingMain } : t
          ),
        }));
      },

      setMainContentForTab: (tabId, mainContent) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, mainContent } : t
          ),
        }));
      },

      setViewingGithub: (tabId, viewingGithub) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, viewingGithub, viewingMain: false } : t
          ),
        }));
      },

      setGithubContentForTab: (tabId, githubContent) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, githubContent } : t
          ),
        }));
      },

      setBundleSiblingsForTab: (tabId, siblings) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, bundleSiblings: siblings } : t
          ),
        }));
      },

      syncTabContentFromServer: (fileId, serverContent, isEdited) => {
        const state = get();
        const tab = state.tabs.find(t => t.file.id === fileId);
        
        if (!tab) {
          // Tab not open, nothing to sync
          return false;
        }
        
        // Always sync from server and reset tab to clean state (as if newly opened)
        set({
          tabs: state.tabs.map(t => 
            t.file.id === fileId
              ? {
                  ...t,
                  content: serverContent,
                  originalContent: serverContent,
                  isDirty: false,  // Reset dirty state
                  isEdited: isEdited ?? t.isEdited,
                }
              : t
          ),
        });
        return true;
      },

      reset: () => {
        set(emptyWorkspace());
      },
    }),
    { name: 'metadata-editor-store' }
  )
);

// ============================================================================
// Organization scoping
// ============================================================================

/** Pull the active workspace out of the flat state. */
function snapshotWorkspace(state: MetadataEditorState): EditorWorkspace {
  return {
    tabs: state.tabs,
    activeTabId: state.activeTabId,
    pendingClose: state.pendingClose,
    visibleMetadataTypes: state.visibleMetadataTypes,
    isFilterModalOpen: state.isFilterModalOpen,
  };
}

/**
 * Park the outgoing organization's workspace and load the incoming one.
 *
 * A workspace with nothing in it is not parked — an org the user opened and left
 * without opening a file should not cost a map entry for the rest of the session.
 * `pendingClose` is dropped rather than parked: it holds a promise resolver for a
 * confirmation dialog that is being unmounted, and reviving it later would resolve
 * a dialog nobody is looking at.
 */
registerOrgScopedState({
  name: 'metadataEditorStore',
  onOrgChange: (nextOrgId, previousOrgId) => {
    useMetadataEditorStore.setState((state) => {
      const outgoing = snapshotWorkspace(state);
      const worthParking = outgoing.tabs.length > 0 || outgoing.visibleMetadataTypes !== null;

      // Note the `else` branch DROPS rather than leaves alone: an org the user
      // has since emptied must not be restored from a parked copy taken on an
      // earlier visit.
      const parked = worthParking
        ? writeOrgSlice(state.workspaceByOrg, previousOrgId, {
            ...outgoing,
            pendingClose: null,
            isFilterModalOpen: false,
          })
        : dropOrgSlice(state.workspaceByOrg, previousOrgId);

      const incoming = readOrgSlice(parked, nextOrgId, emptyWorkspace);
      return { ...incoming, workspaceByOrg: parked };
    });
  },
  onSignOut: clearMetadataEditorWorkspaces,
});

/**
 * Forget one organization's parked workspace. Sign-out only — an org switch
 * parks, it does not destroy.
 */
export function clearMetadataEditorWorkspaces(): void {
  useMetadataEditorStore.setState({
    ...emptyWorkspace(),
    workspaceByOrg: new Map<OrgId, EditorWorkspace>(),
  });
}

// Selectors for better performance
export const selectTabs = (state: MetadataEditorState) => state.tabs;
export const selectActiveTabId = (state: MetadataEditorState) => state.activeTabId;
export const selectActiveTab = (state: MetadataEditorState) => 
  state.tabs.find(t => t.id === state.activeTabId) || null;
export const selectPendingClose = (state: MetadataEditorState) => state.pendingClose;
export const selectVisibleMetadataTypes = (state: MetadataEditorState) => state.visibleMetadataTypes;
export const selectIsFilterModalOpen = (state: MetadataEditorState) => state.isFilterModalOpen;
